import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal, NgbDateParserFormatter, NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { SelectOption } from '../stand-creation-form/types/stand-form.types';
import { DateMaskDirective, dismissDirtyFormModal, noFutureDateValidator, futureDateDisabled, handleDateBlur, ZendraLocalizationServices, ZnConfirmation, ZnSelectQueryParams, ZnSingleSelect } from '@zendra/shared';
import { IModalContent } from 'projects/shared/src/lib/zn-modal-wrapper/interfaces/IModalContent';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { finalize, forkJoin, startWith } from 'rxjs';
import { CreatePlantationFromStandInputDto, PlantationPlanningOrchestratorService } from 'orchestrator';
import { StandDto } from 'projects/orchestrator/src/lib/proxy/upm/zendra/cbd/planning/dtos/stand/models';
import {
  GeneticMaterialSelectOption,
  StandCatalogs,
} from '../stand-creation-form/services/stand-catalogs';
import type { SpeciesDto } from 'projects/cbd-proxies/src/lib/general/dtos/species/models';
import { buildNgbDateToDateOnly } from '../helpers/stand.builder';
import { syncSpeciesWithGeneticMaterialFromForm } from '../helpers/genetic-material-species.helper';
import { CustomDateParserFormatter } from '../../../../../shared/src/lib/utils/custom-date-parser-formatter';

@Component({
  selector: 'lib-action-create-plantation',
  imports: [CommonModule, NgbDatepickerModule, ZnSingleSelect, LocalizationPipe, ReactiveFormsModule, DateMaskDirective],
  templateUrl: './action-create-plantation.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }],
})
export class ActionCreatePlantation implements OnInit, IModalContent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly toaster = inject(ToasterService);
  private readonly activeModal = inject(NgbActiveModal);
  private readonly confirmationService = inject(ZnConfirmation);
  private readonly destroyRef = inject(DestroyRef);
  private readonly localizationService = inject(LocalizationService);
  private readonly plantationService = inject(PlantationPlanningOrchestratorService);
  private readonly catalogService = inject(StandCatalogs)
  
  selectedRecords = input<StandDto[]>([]);
  selectedRecordIds = input<number[]>([]);
  completed = output<void>();

  loading = signal(false);
  loadingScreen = signal(true);
  saving = signal(false);
  plantingTypeOptions = signal<SelectOption<string>[]>([]);
  spacingOptions = signal<SelectOption<string>[]>([]);
  geneticMaterialOptions = signal<GeneticMaterialSelectOption[]>([]);
  species = signal<SpeciesDto>(null);
  disabledDates = futureDateDisabled;
  readonly today = (() => {
    const now = new Date();
    return {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
    };
  })();
  private readonly pendingGeneticMaterialRequests = new Set<string>();

  form: FormGroup = this.formBuilder.group({
    PlantingDate: [null, [Validators.required, noFutureDateValidator()]],
    OwnPlantation: [false],
    PlantingType: [null, [Validators.required]],
    Spacing: [null, [Validators.required]],
    GeneticMaterial: [null, [Validators.required]],
    Species: [{ value: '', disabled: true }],
    PlantingCompleted: [false],
  });
  localizationResource = ZendraLocalizationServices.LanguageService;

  readonly plantingTypesLoader = (params?: ZnSelectQueryParams) => this.catalogService.loadPlantingTypes(params);
  readonly spacingsLoader = (params?: ZnSelectQueryParams) =>this.catalogService.loadSpacings(params);
  readonly geneticMaterialsLoader = (params?: ZnSelectQueryParams) =>this.catalogService.loadGeneticMaterials(params);
  readonly speciesLoader = (params?: ZnSelectQueryParams) =>this.catalogService.loadSpecies(params);

  private readonly formValue = toSignal(this.formValueChanges(), { initialValue: null });
  private readonly formStatus = toSignal(this.formStatusChanges(), {
    initialValue: this.form.status,
  });

  canSave = computed(() => {
    this.formStatus();
    return this.form.valid && !this.loading() && !this.saving();
  });

  readonly syncSpeciesWithGeneticMaterial = effect(() => {
    this.formValue();
    syncSpeciesWithGeneticMaterialFromForm({
      destroyRef: this.destroyRef,
      form: this.form,
      geneticMaterialOptions: this.geneticMaterialOptions,
      geneticMaterialsLoader: this.geneticMaterialsLoader,
      applySpeciesSelection: species => this.applySpeciesSelection(species),
      loading: this.loading,
      pendingRequestIds: this.pendingGeneticMaterialRequests,
    });
  });

  ngOnInit(): void {
    forkJoin({
      geneticMaterials: this.geneticMaterialsLoader({}),
      species: this.speciesLoader({}),
      previousRotationGeneticMaterial: this.plantationService.getPreviousRotationGeneticMaterial(this.selectedRecordIds()[0]),
    })
    .pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.loadingScreen.set(false))
    )
    .subscribe({
      next: result => {
        this.geneticMaterialOptions.set(result.geneticMaterials.items);

        if(result.previousRotationGeneticMaterial && result.previousRotationGeneticMaterial > 0) {
          this.form.get('GeneticMaterial').patchValue(result.previousRotationGeneticMaterial);
          this.form.get('GeneticMaterial').disable({ emitEvent: false });
          this.form.updateValueAndValidity({ emitEvent: false });
        }
      },
      error: error => {
        console.log(error)
        this.dismiss()
      }
    });
  }

  private formStatusChanges() {
    return this.form.statusChanges.pipe(startWith(this.form.status));
  }
  private formValueChanges() {
    return this.form.valueChanges.pipe(startWith(this.form.getRawValue()));
  }

  fieldInvalid(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!ctrl && ctrl.invalid && (ctrl.touched || ctrl.dirty);
  }

  onPlantingDateBlur(inputElement: HTMLInputElement): void {
    handleDateBlur(inputElement, this.form.get('PlantingDate'));
  }

  private applySpeciesSelection(species?: SpeciesDto | null): void {
    const speciesId = species?.id;
    this.species.set(species);

    this.form.get('Species')?.patchValue(speciesId? species.name : null, {emitEvent: false});
    this.form.updateValueAndValidity({ emitEvent: false });

    if (!speciesId) {
      return;
    }
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.loading() || this.saving()) {
      return;
    }

    this.saving.set(true);
    this.loading.set(true);
    const rawData = this.form.getRawValue();
    const date = rawData['PlantingDate'];

    const request: CreatePlantationFromStandInputDto = {
      geneticMaterialId: Number(rawData['GeneticMaterial']),
      isOwnPlantation: rawData['OwnPlantation'],
      isPlantationFinalized: rawData['PlantingCompleted'],
      plantationDate: buildNgbDateToDateOnly(date.day, date.month, date.year),
      plantationTypeId: Number(rawData['PlantingType']),
      spacingId: Number(rawData['Spacing']),
      standId: this.selectedRecordIds()[0]
    };

    this.plantationService.createPlantationFromStand(request)
    .pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        this.loading.set(false);
        this.saving.set(false);
      })
    )
    .subscribe({
      next: result => {
        this.toaster.success(`${this.localizationResource}::Actions:SuccessMessage`, null, {life: 5000, messageLocalizationParams: [this.localizationService.instant(`${ZendraLocalizationServices.Planning}::Action.Modal.CreatePlantation`)] });
        this.completed.emit();
        this.activeModal.close();
      },
      error: error => {
        console.log(error);
      }
    });
  }

  confirm(): void {
    this.save();
  }
  dismiss(): void {
    dismissDirtyFormModal(
      this.form,
      this.saving(),
      this.confirmationService,
      this.activeModal,
    );
  }
}
